import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DotLoading, NoticeBar, Popup, Space, Steps, Tag, Toast } from 'antd-mobile';
import { ClockCircleOutline, TruckOutline } from 'antd-mobile-icons';
import { Step } from 'antd-mobile/es/components/steps/step';

// Import from new structure
import { InterventionJson, InterventionEditJson } from '../../../types/models';
import { apiClient } from '../../../api';
import { faroLogger } from '../../../observability/faro-logger';

// Define ValidationEnum type
type ValidationEnum = 'NONE' | 'VALIDATING' | 'VALID' | 'SEEN' | 'LOOKALIKE';

export interface PopupProps {
  onMaskClick: () => void;
  intervention: InterventionJson;
  /** Où retourner après sauvegarde (défaut : fiche client). Ex. retour au bon d'intervention. */
  returnPath?: string;
}

export const PopupValidation: FC<PopupProps> = (props) => {
  const { onMaskClick, intervention, returnPath } = props;
  const navigate = useNavigate();

  const [validating, setValidating] = useState<ValidationEnum>('NONE');
  const [saving, setSaving] = useState(false);

  const checkValidity = async (intervention: InterventionJson): Promise<void> => {
    setValidating('VALIDATING');

    faroLogger.info('intervention_validation_started', {
      clientId: intervention.client_id,
      immatriculation: intervention.immatriculation,
      parcNumber: intervention.parc_number,
    });

    const result = await apiClient.interventions.validateIntervention({
      immatriculation: intervention.immatriculation,
      parc_number: intervention.parc_number,
      client_id: intervention.client_id,
    });

    if (result.isAlreadyWashedToday) {
      setValidating('SEEN');
      faroLogger.warn('intervention_duplicate_detected', {
        clientId: intervention.client_id,
        immatriculation: intervention.immatriculation,
        parcNumber: intervention.parc_number,
      });
    } else {
      setValidating('VALID');
      faroLogger.info('intervention_validation_success', {
        clientId: intervention.client_id,
        immatriculation: intervention.immatriculation,
        parcNumber: intervention.parc_number,
      });
    }
  };

  const saveIntervention = async (intervention: InterventionJson): Promise<void> => {
    setSaving(true);
    // Ensure intervention.prestation_costs is defined before accessing its properties
    // This might need better handling depending on how prestation_costs is set
    const prestationCostsId = intervention.prestation_costs?.id;
    if (!prestationCostsId) {
      console.error('Prestation costs ID is missing in intervention data.');
      Toast.show({ icon: 'fail', content: 'Données de prestation manquantes.' });
      faroLogger.error('intervention_save_failed', {
        reason: 'missing_prestation_costs',
        clientId: intervention.client_id,
      });
      setSaving(false);
      return;
    }

    const payload: InterventionEditJson = {
      driver: intervention.driver || '',
      immatriculation: intervention.immatriculation || '',
      parc_number: intervention.parc_number || '',
      immatriculation_bis: intervention.immatriculation_bis,
      parc_number_bis: intervention.parc_number_bis,
      interior: !!intervention.interior,
      exterior: !!intervention.exterior,
      disinfection: !!intervention.disinfection,
      decontamination: !!intervention.decontamination,
      package: !!intervention.package,
      comment: intervention.comment || '',
      client_id: intervention.client_id,
      prestation_costs: intervention.prestation_costs,
      photo_plate: intervention.photo_plate,
      timestamp: intervention.timestamp,
      author_id: intervention.author_id,
    };

    try {
      if (intervention.id) {
        await apiClient.interventions.updateIntervention(intervention.id, payload);
        Toast.show({ icon: 'success', content: 'Intervention mise à jour.' });
        faroLogger.info('intervention_updated', {
          interventionId: intervention.id,
          clientId: intervention.client_id,
          vehicleType: intervention.prestation_costs?.title,
          prestations: {
            interior: intervention.interior,
            exterior: intervention.exterior,
            disinfection: intervention.disinfection,
            decontamination: intervention.decontamination,
            package: intervention.package,
          },
        });
        navigate(returnPath ?? `/client/${intervention.client_id}`);
      } else {
        await apiClient.interventions.createIntervention(payload);
        Toast.show({ icon: 'success', content: 'Intervention créée.' });
        faroLogger.info('intervention_created', {
          clientId: intervention.client_id,
          vehicleType: intervention.prestation_costs?.title,
          prestations: {
            interior: intervention.interior,
            exterior: intervention.exterior,
            disinfection: intervention.disinfection,
            decontamination: intervention.decontamination,
            package: intervention.package,
          },
          hasPhoto: !!intervention.photo_plate,
        });
        navigate(returnPath ?? `/client/${intervention.client_id}`);
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'intervention:", error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      Toast.show({
        icon: 'fail',
        content: `Échec de la sauvegarde: ${errorMessage}`,
      });
      faroLogger.error('intervention_save_failed', {
        clientId: intervention.client_id,
        error: errorMessage,
        isUpdate: !!intervention.id,
      });
    } finally {
      setSaving(false); // Ensure saving state is reset regardless of success or error
    }
  };

  useEffect(() => {
    checkValidity(intervention);
  }, [intervention]);

  return (
    <Popup visible onMaskClick={onMaskClick} bodyStyle={{ height: '50vh' }}>
      <Steps
        direction="vertical"
        current={3}
        style={{
          '--title-font-size': '17px',
          '--description-font-size': '15px',
          '--indicator-margin-right': '12px',
          '--icon-size': '32px',
        }}
      >
        <Step
          title={`${intervention.prestation_costs.title} - ${
            intervention.immatriculation ? intervention.immatriculation : ''
          } ${intervention.parc_number ? ' - ' + intervention.parc_number : ''}
          ${intervention.immatriculation_bis ? ' | ' + intervention.immatriculation_bis : ''}
          ${intervention.parc_number_bis ? ' - ' + intervention.parc_number_bis : ''}`}
          description={
            <Space direction="horizontal">
              {/* <Switch onChange={(checked) => setValidating(checked)} /> */}
              {intervention.exterior && (
                <Tag round color="primary">
                  Exter.
                </Tag>
              )}
              {intervention.disinfection && (
                <Tag round color="danger">
                  Desinf.
                </Tag>
              )}
              {intervention.interior && (
                <Tag round color="success">
                  Inter.
                </Tag>
              )}
              {intervention.decontamination && (
                <Tag round color="warning">
                  Decont.
                </Tag>
              )}
              {intervention.package && (
                <Tag round color="default">
                  Pack.
                </Tag>
              )}
            </Space>
          }
          icon={<TruckOutline />}
          status="finish"
        />
        <Step
          title={new Intl.DateTimeFormat('fr', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }).format(Date.parse(intervention.timestamp))}
          icon={<ClockCircleOutline />}
        />

        {validating === 'VALIDATING' && (
          <Step
            status="process"
            title={
              <>
                <DotLoading color={'primary'} /> checking
              </>
            }
          />
        )}
        {validating === 'VALID' && <Step status="finish" title={<>Ready</>} />}
        {validating === 'SEEN' && (
          <Step
            status="finish"
            title={
              <>
                <NoticeBar color="alert" content="Ce véhicule a déjà été lavé aujourd'hui" />
              </>
            }
          />
        )}
        {/* {validating === 'LOOKALIKE' && (
          <Step
            status="process"
            title={
              <>
                <NoticeBar color="info" content="J'ai trouvé des immat proches" />
                {similarImmat.map((immat) => (
                  <Button
                    key={immat.immatriculation}
                    onClick={() => {
                      form.setFieldsValue({ immatriculation: immat.immatriculation });
                      checkValidity({ ...values, immatriculation: immat.immatriculation });
                    }}
                  >
                    {immat.immatriculation}{' '}
                  </Button>
                ))}
              </>
            }
          />
        )} */}
        {saving ? (
          <Step
            status="process"
            title={
              <>
                <DotLoading color={'primary'} /> Envoi
              </>
            }
          />
        ) : (
          <Step
            status="wait"
            title={
              <Button
                onClick={() => saveIntervention(intervention)}
                color={validating === 'SEEN' ? 'danger' : 'primary'}
              >
                Envoyer
              </Button>
            }
          />
        )}
      </Steps>
    </Popup>
  );
};
